import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { PartialEntityData } from '@adornis/baseql/entities/adornisEntity.js';
import { ExternerMitarbeiter } from '@chancenwerk/master-data-management/users/entities/ExternerMitarbeiter.js';
import { DateTime } from 'luxon';
import { Course } from './Course.js';
import type { Subject } from './CourseDigital.js';

/**
 * @enum CourseAbschlussType
 *
 * The type of a course of the CHANCENabschluss
 */
export enum CourseAbschlussType {
  HAUPTSCHULABSCHLUSS = 'Hauptschulabschluss',
  MITTLERER_BILDUNGSABSCHLUSS = 'Mittlerer Bildungsabschluss',
  EF = 'EF',
  ABITUR = 'Abitur',
  FACHHOCHSCHULREIFE = 'Fachhochschulreife',
}

/**
 * @entity CourseAbschluss
 *
 * A course of the CHANCENabschluss
 */
@Entity()
export class CourseAbschluss extends Course {
  static override _class = 'CourseAbschluss';

  constructor(doc: PartialEntityData<CourseAbschluss>) {
    super(doc);
  }

  @Field(type => DateTime)
  endDate: Maybe<DateTime>;

  @Field(type => String)
  diKuLink: Maybe<string>;

  @DataLoadedField(type => [ExternerMitarbeiter], ({ diKuIDs }) => diKuIDs ?? [])
  diKus: Maybe<ExternerMitarbeiter[]>;

  /**
   * The IDs of the DigitaleKursleitung that are part of this course
   */
  @Field(type => [String], { default: v => v ?? [] })
  diKuIDs: Maybe<string[]>;

  /**
   * Whether the course is open to all KPFs
   */
  @Field(type => Boolean)
  openToAll: Maybe<boolean>;

  /**
   * The subject of the course
   */
  @Field(type => String)
  subject: Maybe<Subject>;

  /**
   * The type of the course, like "Hauptschulabschluss" or "Abitur"
   */
  @Field(type => String)
  type: Maybe<CourseAbschlussType>;

  /**
   * The target class for the course (Jahrgangsstufe)
   */
  @Field(type => Int)
  class: Maybe<number>;

  /**
   * The maximum number of attendees for the course
   */
  @Field(type => Int)
  maxAttendanceCount: Maybe<number>;

  /**
   * If changes to the course were made and a notification should be shown
   */
  @Field(type => Boolean, { default: v => v ?? false })
  changeNotification?: boolean;
}
